/* eslint-disable max-len */
import { map, get, clone } from 'lodash';
import BaseClient from '@core/common/BaseClient';
import BaseService from '@core/common/BaseService';
import { Subscription } from '@core/common/Subscription';
import { ACClient, RESOURCES, EVENTS } from '@core/clients/AcClient';
import acTransformer from './acTransformer';

const cancellationTokens = {};
const DUMMY_REVISION = '-1';
const RESOURCES_EXCLUDING_SITE_SETTINGS = clone(RESOURCES);
delete RESOURCES_EXCLUDING_SITE_SETTINGS.SITE_SETTINGS;

function createAcClients(settings) {
  const usersClient = new ACClient({
    resource: RESOURCES.USERS,
    ...settings,
  });
  const skillsClient = new ACClient({
    resource: RESOURCES.SKILLS,
    ...settings,
  });
  const agentGroupsClient = new ACClient({
    resource: RESOURCES.AGENT_GROUPS,
    ...settings,
  });
  const userManagedDataClient = new ACClient({
    resource: RESOURCES.USER_MANAGED_DATA,
    ...settings,
  });
  const statusReasonClient = new ACClient({
    resource: RESOURCES.STATUS_REASON,
    ...settings,
  });
  const siteSettingsClient = new ACClient({
    resource: RESOURCES.SITE_SETTINGS,
    ...settings,
  });

  const RESOURCES_TO_CLIENT_MAP = {
    [RESOURCES.USERS]: usersClient,
    [RESOURCES.SKILLS]: skillsClient,
    [RESOURCES.AGENT_GROUPS]: agentGroupsClient,
    [RESOURCES.USER_MANAGED_DATA]: userManagedDataClient,
    [RESOURCES.STATUS_REASON]: statusReasonClient,
    [RESOURCES.SITE_SETTINGS]: siteSettingsClient,
  };

  const getClient = resourceName => RESOURCES_TO_CLIENT_MAP[resourceName];

  return {
    getClient,
  };
}

function stopListenToAcClientEvents(resourceName) {
  const client = this.acClients.getClient(resourceName);
  client.clearAllListeners(EVENTS.onRevisionedPollingSuccess);
  client.clearAllListeners(EVENTS.onRevisionedPollingError);
}

async function enrichAcData(settings, resourceName, acResponse, cancellationToken) {
  cancellationTokens[resourceName] = cancellationToken;
  if (acResponse && acResponse.status === BaseClient.RESPONSE_STATUSES.SUCCESS) {
    const revision = acResponse.parsed.meta.revision;
    const enrichedAcResponse = acTransformer.transformResponse(resourceName, { acResponse });
    const resourceSubscription = this.subscriptions.find(
      subscriptionRecord => subscriptionRecord.name === resourceName);
    resourceSubscription.subscription.emit(Subscription.EVENTS.onSubscriptionUpdateNotification,
      { name: resourceName, data: { data: enrichedAcResponse, revision } });
  }
}

function onAcSubscriptionError(resourceName, error) {
  const resourceSubscription = this.subscriptions.find(
    subscriptionRecord => subscriptionRecord.name === resourceName);
  resourceSubscription.subscription.emit(Subscription.EVENTS.onSubscriptionError, error);
}

async function dummyPromiseForLPA() {
  return Promise.resolve();
}

async function fetchAcEntities(settings) {
  const acEntityRequest = acTransformer.prepareRequest(settings);
  const firstACRequestMaxRetries = 3;
  const isLPA = settings.isLPA;
  const [usersClient, skillsClient, agentGroupsClient, userManagedDataClient, statusReasonDataClient] = [
    this.acClients.getClient(RESOURCES.USERS),
    this.acClients.getClient(RESOURCES.SKILLS),
    this.acClients.getClient(RESOURCES.AGENT_GROUPS),
    isLPA ? null : this.acClients.getClient(RESOURCES.USER_MANAGED_DATA),
    this.acClients.getClient(RESOURCES.STATUS_REASON)];

  async function sendFirstACRequests(retryCount) {
    if (retryCount <= firstACRequestMaxRetries) {
      const usersPromise = this.sendClientRequest(usersClient, acEntityRequest, 'request to AC-users API failed');
      const skillsPromise = this.sendClientRequest(skillsClient, acEntityRequest, 'request to AC-skills API failed');
      const agentGroupsPromise = this.sendClientRequest(agentGroupsClient, acEntityRequest, 'request to AC-agent-groups API failed');
      const userManagedDataPromise = isLPA ? dummyPromiseForLPA() : this.sendClientRequest(userManagedDataClient, acEntityRequest, 'request to AC-user-managed-data API failed');
      const statusReasonDataPromise = this.sendClientRequest(statusReasonDataClient, acEntityRequest, 'request to AC-status-reasons API failed');

      return Promise.all(
        [usersPromise, skillsPromise, agentGroupsPromise, userManagedDataPromise, statusReasonDataPromise]).then(
        (response) => {
          const data = {};
          Object.keys(RESOURCES_EXCLUDING_SITE_SETTINGS).forEach((key, index) => {
            const buildResponseDataForLPA = isLPA && RESOURCES[key] === RESOURCES.USER_MANAGED_DATA;
            const responseData = buildResponseDataForLPA ? {
              userList: response[0] && response[0].data ? map(response[0].data, user => user.id) : [],
              skillList: response[1] && response[1].data ? map(response[1].data, skill => skill.id) : [],
              agentGroups: response[2] && response[2].data ? map(response[2].data, agentGroup => agentGroup.id) : [],
            } : response[index] && response[index].data;
            const revision = buildResponseDataForLPA ? DUMMY_REVISION : get(response[index], 'parsed.meta.revision');
            const transformedResponse = acTransformer.transformResponse(RESOURCES[key],
              { responseData });
            data[RESOURCES[key]] = { data: transformedResponse, revision };
          });
          return data;
        }).catch(
        (err) => {
          this.logError(`Error on first AC-Entities fetch: ${err}`);
          this.logError(`Going to retry first AC-Entities fetch. Retries Left: ${firstACRequestMaxRetries - retryCount}`);
          return sendFirstACRequests.call(this, retryCount + 1);
        });
    }
    this.logError('Error on first AC-Entities fetch: Reached max amount of retries');
    return null;
  }
  return sendFirstACRequests.call(this, 0);
}

function listenToAcClientEvents(settings, resourceName) {
  stopListenToAcClientEvents.call(this, resourceName);
  const client = this.acClients.getClient(resourceName);
  client.on(
    EVENTS.onRevisionedPollingSuccess,
    enrichAcData.bind(this, settings, resourceName),
    'acServiceRevisionPollingSuccess');
  client.on(
    EVENTS.onRevisionedPollingError, onAcSubscriptionError.bind(this, resourceName),
    'acServiceRevisionPollingError');
}

async function subscriptionStartMethod(settings, resourceName) {
  const request = acTransformer.prepareRequest(settings);
  listenToAcClientEvents.call(this, settings, resourceName);
  cancellationTokens[resourceName] = await this.acClients.getClient(resourceName).subscribe(request);
}

function subscriptionStopMethod(resourceName) {
  const token = get(cancellationTokens, resourceName);
  if (token) {
    stopListenToAcClientEvents.call(this, resourceName);
    this.acClients.getClient(resourceName).unsubscribe(token);
  }
}

/**
 * Account Config service - retrieves the AC entities data for all of the widgets and filters.
 * Requests data 4 different APIs:
 * AC-Users (Get all)
 * AC-Users (Get User Managed Data)
 * AC-Skills (Get all)
 * AC-AgentGroups (Get all)
 *
 */
class ACService extends BaseService {
  constructor() {
    super({ name: 'ACService' });
  }

  async getACPackage(settings) {
    if (!this.subscriptions && !this.acClients) {
      this.acClients = createAcClients(settings);
    }
    let data;
    if (!this.subscriptions || settings.forceFetchAcEntities) {
      data = await fetchAcEntities.call(this, settings);
    }
    if (!this.subscriptions) {
      this.subscriptions = [];
      Object.keys(RESOURCES_EXCLUDING_SITE_SETTINGS).forEach((key) => {
        const resourceName = RESOURCES[key];
        const generateSubscription = !(settings.isLPA && resourceName === RESOURCES.USER_MANAGED_DATA);
        if (generateSubscription) {
          const resourceSubscription = new Subscription({
            name: `${resourceName}Subscription`,
            resourceName,
            handlers: {
              start: subscriptionStartMethod.bind(this, settings, resourceName),
              stop: subscriptionStopMethod.bind(this, resourceName),
            },
          });
          this.subscriptions.push({ name: resourceName, subscription: resourceSubscription });
        }
      });
    }
    return { subscriptions: this.subscriptions, data };
  }

  async setSiteSetting(key, value, revision, loggedInUserId) {
    const requestSettings = acTransformer.transformSetSiteSettingRequest({ value, revision });
    let setUserSiteSettingResponse;
    try {
      if (!this.acClients) {
        this.acClients = createAcClients(loggedInUserId);
      }
      const siteSettingsClient = this.acClients.getClient(RESOURCES.SITE_SETTINGS);
      setUserSiteSettingResponse = await this.sendClientRequest(
        siteSettingsClient,
        requestSettings,
        `Can't update ${key} in site settings`,
      );
      const responseRevision = get(setUserSiteSettingResponse, 'headers.ac-revision');
      const data = acTransformer.transformSetUserSiteSettingResponse(setUserSiteSettingResponse);
      setUserSiteSettingResponse = { data, responseRevision };
    } catch (error) {
      setUserSiteSettingResponse = Promise.reject(error);
      this.logError(`Error setting site setting ${key}`);
    }
    return setUserSiteSettingResponse;
  }

  async getManagerWorkspaceSiteSettings() {
    const requestSettings = acTransformer.transformFetchSiteSettingsRequest();
    let response;
    try {
      const siteSettingsClient = this.acClients.getClient(RESOURCES.SITE_SETTINGS);
      response = await this.sendClientRequest(
        siteSettingsClient,
        requestSettings,
        'request to AC-site-settings API failed');
      const revision = get(response, 'parsed.meta.revision');
      const data = acTransformer.transformFetchSiteSettingsResponse(response);
      response = { data, revision };
    } catch (error) {
      response = Promise.reject(error);
      this.logError('Error fetching Manager Workspace site settings');
    }
    return response;
  }
}

const acService = new ACService();

export default acService;
